import { CalendarCheck } from 'lucide-react';
import { MoldOrder, MoldOrderStatus } from '../../types/mold';
import { formatShortThaiDate, getRemainingDaysInThai } from '../../utils/dateUtils';
import { getPriorityBgColor, getPriorityLabel } from '../MoldDetail/utils';
import UserProfileImage from './UserProfileImage';
import MoldOrderCardTypeLabel from '../shared/MoldOrderCardTypeLabel';
import MoldOrderDueDateLabel from '../shared/MoldOrderDueDateLabel';
import { useAppState } from '@/context/AppStateContext';

interface MoldOrderCardProps {
  onClick: () => void;
  moldOrder: MoldOrder;
}
const MoldOrderCard: React.FC<MoldOrderCardProps> = ({
  moldOrder,
  onClick,
}) => {
  if (!Boolean(moldOrder)) return <></>;
  const isInFinalState = [
    MoldOrderStatus.DONE,
    MoldOrderStatus.END_REJECTED,
  ].includes(moldOrder.status);
  const { getKanbanCardHeadlines } = useAppState();
  const headlines = getKanbanCardHeadlines(moldOrder);
  const bgColor = getPriorityBgColor(moldOrder.priority);
  const getDisplayStatusColor = (status: 'red' | 'yellow' | 'green') => {
    if (isInFinalState) return 'text-gray-600';
    return status === 'red' ? 'text-red-600' : status === 'yellow' ? 'text-yellow-600' : 'text-green-600';
  }
  const { status } = getRemainingDaysInThai(moldOrder.orderDetail.dueDate ?? '');
  const statusColor = getDisplayStatusColor(status);
  const borderColor = isInFinalState ? (
    moldOrder.status === MoldOrderStatus.DONE ? 'border-b-4 border-green-500' : 'border-b-4 border-red-500'
  ) : '';
  const displayFullName = moldOrder?.assignedTo?.fullname?.split(' ')[0] || moldOrder?.assignedTo?.username || 'ไม่ระบุ';
  const assignee = moldOrder.assignedTo
    ? { ...moldOrder.assignedTo, name: moldOrder.assignedTo.fullname || moldOrder.assignedTo.username }
    : null;
  return (
    <div
      className={`${bgColor} p-4 mb-4 rounded-lg shadow-lg hover:shadow-xl text-lg min-h-[100px] cursor-pointer ${borderColor}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <div className="flex flex-row justify-between items-center pb-1 truncate max-w-full">
        <span>
          {headlines.title}
        </span>
        {moldOrder.priority <= 100 && <span className="text-xs font-bold bg-red-400 text-white px-2 py-1 rounded-lg">{getPriorityLabel(moldOrder.priority)}</span>}
      </div>
      {headlines.subtitle && (
        <div className="flex justify-start items-center pb-3 text-xs">
          <div
            className="truncate max-w-full"
            title={headlines.subtitle}
          >
            {headlines.subtitle}
          </div>
        </div>
      )}
      <div className="flex justify-between items-center pb-2 text-sm">
        <span>รหัสใบงาน #{moldOrder.id}</span>
        <MoldOrderCardTypeLabel moldOrder={moldOrder} />
      </div>
      {isInFinalState && moldOrder.updatedAt && <div className={`flex justify-start items-start text-xs gap-1 ${statusColor}`}>
        <CalendarCheck size={12} className={statusColor} />
        <span>เสร็จ: {formatShortThaiDate(new Date(moldOrder.updatedAt))}</span>
      </div>}
      <MoldOrderDueDateLabel moldOrder={moldOrder} />
      <div className="flex justify-between items-center">
        <span className="flex flex-row space-x-1 w-full justify-between">
          <div className='flex flex-row items-center space-x-1'>
            {moldOrder.orderDetail.machineToProduce && <div className="flex justify-start gap-1 items-center text-xs bg-teal-100 text-teal-800 p-2 rounded-full">
              <span>M/C {moldOrder.orderDetail.machineToProduce}</span>
            </div>}
            {moldOrder?.mold?.moldNumber && <span className="text-xs p-2 rounded-full bg-blue-100 text-blue-800">
              {moldOrder?.mold?.moldNumber}
            </span>}
          </div>
          <div className='flex flex-row items-center space-x-1'>
            <UserProfileImage currentAssignee={assignee} size='sm' />
            <p className='text-xs'>{displayFullName}</p>
          </div>
        </span>
      </div>
      {isInFinalState && (<div className="flex justify-between items-center mt-3">
        <span className='w-full'>
          <div
            className={`text-sm ${moldOrder.status === MoldOrderStatus.DONE ? 'text-green-600' : 'text-red-600'}`}
          >
            ●{' '}
            {moldOrder.status === MoldOrderStatus.DONE
              ? 'อนุมัติแล้ว'
              : 'ไม่อนุมัติ'}
          </div>
        </span>
      </div>)}
    </div>
  );
};

export default MoldOrderCard;
